<template>
  <v-dialog :value="value" persistent width="800">
    <v-card class="fill">
      <v-toolbar flat dark class="main">
        <v-toolbar-title> {{ existing ? "Edit Recap Addition" : "New Recap Addition" }}  </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$emit('close', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container class="ma-0 px-4" fluid>
        <v-form ref="form" lazy-validation>
          <v-row class="mt-0 pt-0">
            <v-col>
              <v-autocomplete
                v-model="recapAddition.recap_component_id"
                label="Recap Component"
                :items="recapComponents"
                background-color="input"
                :menu-props="{ offsetY: true }"
                item-text="name"
                item-value="id"
                :disabled="existing"
                :rules="[v => !!v || 'Recap component is required']"
                outlined
                dense>
              </v-autocomplete>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="recapAddition.party_id"
                label="Store"
                :items="parties"
                background-color="input"
                item-text="name"
                item-value="id"
                :disabled="!!file || existing"
                :rules="[v => !!v || !!file || 'Store is required']"
                outlined
                :loading="loadingItems"
                :menu-props="{ maxHeight: 215, maxWidth: 650 }"
                dense
                single-line
                auto-select-first
                :clearable="!existing">
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="mt-0 pt-0">
            <v-col>
              <v-autocomplete
                v-model="recapAddition.billing_year_id"
                label="Billing Year"
                :items="billingYears"
                background-color="input"
                :menu-props="{ offsetY: true }"
                item-text="name"
                item-value="id"
                :rules="[v => !!v || 'Billing Year is required']"
                outlined
                dense>
              </v-autocomplete>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="recapAddition.billing_period_id"
                label="Billing Period"
                :items="billingPeriods"
                background-color="input"
                :menu-props="{ offsetY: true }"
                item-text="name"
                item-value="id"
                :rules="[v => !!v || 'Billing Period is required']"
                outlined
                dense>
              </v-autocomplete>
            </v-col>
            <v-col>
              <v-text-field
                v-model="recapAddition.amount"
                label="Amount"
                background-color="input"
                outlined
                dense
                type="number"
                min="0.00"
                step=".01"
                prefix="$"
                :disabled="!!file"
                :rules="[v => !!v || !!file || 'Amount is required']"
                hide-details="auto">
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="pt-0 mt-0">
            <v-col>
              <v-text-field
                v-model="recapAddition.comment"
                label="Comment"
                background-color="input"
                outlined
                dense
                :rules="[v => !!v || 'Comment is required', validateText]">
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-0 pt-0 mb-2" v-if="!existing">
            <v-col>
              <v-file-input
                v-model="file"
                outlined 
                dense
                hide-details="auto" 
                truncate-length="100"
                label="File"
                background-color="input"
                clearable
                accept=".xlsx"
                :disabled="disableFileUpload"
                @change="onFileChange">
              </v-file-input>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-divider/>
      <v-card-actions>
      <v-row dense>
        <v-spacer/>
        <v-btn
          :loading="downloading"
          :disabled="downloading"
          color="primary" 
          class="ma-2 white--text" 
          @click="downloadTemplate()">
          <v-icon left>mdi-file-download-outline</v-icon>
          Template
        </v-btn>
        <v-btn 
          class="ma-2"
          color="success"
          @click="existing ? saveRecapAddition() : createRecapAddition()" 
          :loading="saving"
          :disabled="saving || disableSave">
          {{ existing ? 'Save' : 'Create' }}
        </v-btn>
      </v-row> 
    </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
// api
import RecapAdditions from '@/axios/recap-additions'
// mixins
import { dateFormat } from '@/mixins/date-format'
import { displayAlert } from '@/mixins/displayAlert'
import { validation } from "@/mixins/validation";
import { utils } from '@/mixins/utils'
import { userAccess } from '@/mixins/user-access'
import readXlsxFile from 'read-excel-file'
export default {
  data () {
    return {
      file: null,
      filePayload: {},
      loading: false,
      loadingItems: false,
      downloading: false,
      saving: false,
      recapAddition: {
        amount: '',
        billing_year_id: null,
        billing_period_id: null,
        recap_component_id: null,
        party_id: null,
        file_name: '',
        comment: '',
      },
      recapAdditionsToAdd: [],
      recapAdditionAdGroups: [],
      selectedAdGroups: [],
      selected: [],
      search: '',
      selectedCategoryIds: [],
      uploadErrors: []
    }
  },
  name: 'EditRecapAddition',
  props: ['value', 'selectedRecapAddition'],
  mixins: [dateFormat, displayAlert, utils, validation, userAccess],
  created () {
    if (this.selectedRecapAddition) {
      this.recapAddition = { ...this.recapAddition, ...this.selectedRecapAddition }
    }
  },
  computed: {
    disableSave () {
      const requiredFieldsSelected = this.recapAddition.recap_component_id && this.recapAddition.billing_year_id && this.recapAddition.billing_period_id && this.recapAddition.comment
      return (!this.file && (!requiredFieldsSelected || !this.recapAddition.amount || !this.recapAddition.party_id)) || (this.file && !requiredFieldsSelected) || this.uploadErrors.length > 0
    },
    disableFileUpload () {
      return !this.recapAddition.recap_component_id || !this.recapAddition.billing_year_id || !this.recapAddition.billing_period_id || !this.recapAddition.comment
    },
    billingPeriods () {
      return this.$store.getters.billingPeriods || []
    },
    billingYears () {
      return this.$store.getters.billingYears || []
    },
    recapComponents () {
      const components = this.$store.getters.recapComponents
      if (components?.length > 0) {
        return components.filter(comp => comp.component_type === 'MANUAL')
      }
      return []
    },
    recapAddStatuses () {
      return this.$store.getters.recapAddStatuses || []
    },
    pendingRecapStatus () {
      if (this.recapAddStatuses?.length > 0) {
        return this.recapAddStatuses.find(s => s.constant === 'PENDING')
      }
      return null
    },
    stores () {
      return this.$store.getters.stores || []
    },
    validStores () {
      if (this.stores?.length > 0) {
        return this.stores.filter(store => {
          const endDate = store.end_date ? this.getNormalizedDate(store.end_date) : null
          return !endDate || (endDate >= this.minEndDate)
        })
      }
      return []
    },
    parties() {
      if (this.existing) {
        return [this.selectedRecapAddition.party]
      }
      return this.validStores
    },
    existing () {
      return !!this.selectedRecapAddition?.recap_addition_id
    },
    currentDate() {
      return this.getNormalizedDate()
    },
    minEndDate() {
      const minDate = this.getNormalizedDate()
      minDate.setDate(this.currentDate.getDate() - 56) // 8 weeks
      return minDate
    }
  },
  methods: {
    onFileChange(file) {
      this.recapAdditionsToAdd = []
      this.uploadErrors = []
      this.recapAddition.amount = null
      this.recapAddition.party_id = null
      if (file) {
        readXlsxFile(file).then((rows) => {
          rows.shift()
          const toAdd = []
          const missingStoreNumbers = []
          const unusableAmounts = []
          rows.forEach(row => {
            const store = this.stores.find(store => parseInt(store.wholesaler_store_number) === parseInt(row[0]))
            const amount = parseFloat(String(row[1]).replace(/[$,]+/g,""))
            if (!store) {
              missingStoreNumbers.push(row[0])
            }
            if (!amount || amount === 'NaN') {
              unusableAmounts.push(row[1])
            }
            if (store && amount && amount !== 'NaN') {
              const ra = {
                party_id: store.store_party_id,
                amount: amount,
                recap_component_id: this.recapAddition.recap_component_id,
                billing_year_id: this.recapAddition.billing_year_id,
                billing_period_id: this.recapAddition.billing_period_id,
                comment: this.recapAddition.comment,
                file_name: this.file.name
              }
              toAdd.push(ra)
            }
          })
          if (missingStoreNumbers.length > 0) this.uploadErrors.push(`Failed to find the following store(s): ${missingStoreNumbers.join(', ')}`)
          if (unusableAmounts.length > 0) this.uploadErrors.push(`Failed to parse the following amount(s): ${unusableAmounts.join(', ')}`)
          if (!this.uploadErrors.length > 0) this.recapAdditionsToAdd = toAdd
          else {
            this.$store.dispatch('setSnackbar', { status: 'error', text: `${this.uploadErrors.join('\r\n')}` })
          }
        })
      }
    },
    async createRecapAddition () {
      const isValid = this.validate()
      if (!isValid) {
        return
      }
      this.saving = true
      try {
        if (!this.file) {
          const payload = this.buildRecapAdditionPayload()
          payload.recap_addition_status_id = this.pendingRecapStatus.id
          await RecapAdditions.post(payload)
        } else {
          const promises = this.recapAdditionsToAdd.map(ra => {
            return RecapAdditions.post(ra)
          })
          const { rejected } = await this.getAllSettled(promises, true)
          if (rejected?.length > 0) throw rejected
        }
        this.emitAlert(true, 'success', `Successfully created new recap ${ this.file ? 'additions' : 'addition' }.`)
        this.$emit('close', true)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
       }
    },
    async saveRecapAddition () {
      const isValid = this.validate()
      if (!isValid) {
        return
      }
      this.saving = true
      try {
        await RecapAdditions.put(this.recapAddition.recap_addition_id, this.recapAddition)
        this.emitAlert(true, 'success', `Successfully saved recap addition.`)
        this.$emit('close', true)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
       }
    },
    buildRecapAdditionPayload () {
      const recapAddition = {
        ...this.recapAddition,
        file_name: this.file?.name ? this.file?.name : null,
      }
      return recapAddition
    },
    async downloadTemplate () {
      try {
        this.emitAlert(true, 'info', 'Template downloading.', [], true)
        this.downloading = true
        const response = await RecapAdditions.downloadTemplate()
        if (response?.data?.url) {
          const url = response.data.url
          window.open(url, '_blank')
        }
      } catch (err) {
        console.error(err)
        this.emitAlert(true, 'error', 'Error while downloading template.')
      }
      this.downloading = false
    },
    validate () {
      return this.$refs.form.validate()
    },
    close () {
      this.$emit('close')
    },
  }
}
</script>
<style>
.v-text-field__slot input::-webkit-calendar-picker-indicator {
  display: none;
}
.v-text-field__slot input[type="date"] {
  cursor: text;
}
.v-text-field__slot input[type="date"]::-webkit-input-placeholder {
  visibility: hidden;
}
</style>